import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import {
  AttachmentGallery,
  Avatar,
  Alert,
  backLinkClasses,
  Badge,
  Button,
  buttonClasses,
  Card,
  cardClasses,
  DetailSection,
  DetailSections,
  Fact,
  Facts,
  Icon,
  linkTextClasses,
  NotProvided,
  Page,
  PageHeader,
  Table,
  type Action,
  type IconName,
  isNotFound,
} from '@evogard/ui';
import { DataCell } from '../DataCell.js';
import { ClosureBadge } from '../closures/closureLabels.js';
import {
  claimClosureKindOf,
  formatDocument,
  formatInstant,
  formatPhone,
  thirdPartyQuestions,
} from '@evogard/domain';
import { ClaimForm } from './ClaimForm.js';
import {
  CAMERAS_OPTIONS,
  CAUSED_BY_OPTIONS,
  CLAIM_TYPE_OPTIONS,
  DEPARTMENT_LABEL,
  labelOf,
  phaseLabel,
  REPORTED_THIRD_PARTIES_OPTIONS,
  THIRD_PARTY_DAMAGE_OPTIONS,
  TOW_DESTINATION_OPTIONS,
  YES_NO,
  type Option,
} from './labels.js';
import { noticeAttachmentItem } from './noticeFiles.js';
import {
  useClaim,
  useClaimMutations,
  useClaimPermissions,
  type Claim,
} from './useClaims.js';
import { MissingRecord } from '../../../components/MissingRecord.js';

/**
 * A ficha de um sinistro: onde ele está, o carro, o que foi contado — e, no do
 * associado, os sinistros de terceiro ligados a ele.
 *
 * ## A mesma forma do formulário (25/09/2026)
 *
 * A ficha lê o que o `ClaimForm` escreveu, e por isso tem as MESMAS seções,
 * com os mesmos ícones e títulos, e o trilho ao lado (`DetailSections`). Quem
 * preencheu "O reboque" com a bandeira acha o reboque pela bandeira. Em cima
 * delas, o resumo: quem é, onde o sinistro está, e os quatro dados que se
 * procuram primeiro — o carro, o tipo, quando e o protocolo.
 *
 * ## O sinistro do terceiro nasce aqui
 *
 * "Adicionar terceiro envolvido" abre o formulário do sinistro do terceiro já
 * ligado a este (`parent_claim_id`), com o que é da mesma ocorrência trazido
 * daqui — o protocolo, quando, onde e o reboque; a versão do terceiro começa
 * vazia (ver `ClaimForm`) — e editável. É o caminho que o dono escolheu em
 * 24/09: o pai fica implícito, e ninguém precisa procurar o sinistro do
 * associado numa lista. Aberto, o formulário toma o lugar da ficha, como o de
 * corrigir: são sete etapas com trilho próprio, e a ficha embaixo só
 * empurraria o fim da página.
 *
 * ## Arquivar respeita o par
 *
 * O do associado não arquiva com sinistro de terceiro ativo, e o do terceiro
 * não reativa com o do associado arquivado. A API recusa, e a recusa aparece
 * aqui em cima — a ficha não esconde o botão, porque a razão da recusa é a
 * informação que a pessoa precisa.
 */
export function ClaimScreen() {
  const { id } = useParams({ from: '/authenticated/claims/$id' });
  const claimId = Number(id);
  const navigate = useNavigate();
  const { canEdit } = useClaimPermissions();
  const query = useClaim(Number.isInteger(claimId) ? claimId : null);
  const { update } = useClaimMutations();
  const [editing, setEditing] = useState(false);
  const [addingThirdParty, setAddingThirdParty] = useState(false);

  const claim = query.data;
  const viewing = claim && !editing && !addingThirdParty;
  /* O encerrado é só para consulta (22/10/2026): corrigir o aviso pede
     reabrir. Arquivar continua — tira da operação, não muda o registrado. */
  const closed =
    claim !== undefined &&
    claimClosureKindOf(claim.currentDepartment, claim.currentPhase) !== null;

  if (!Number.isInteger(claimId) || isNotFound(query.error)) {
    return (
      <MissingRecord
        title="Este sinistro não existe nesta base"
        list={{ to: '/claims', label: 'Ver os sinistros' }}
      />
    );
  }

  return (
    <Page measure="cheia">
      <Link to="/claims" className={backLinkClasses()}>
        Sinistros
      </Link>

      <PageHeader
        title={
          claim ? `Sinistro #${claim.id}` : query.isLoading ? 'Carregando…' : 'Sinistro'
        }
        actions={
          viewing &&
          canEdit && (
            <div className="flex gap-2">
              {!closed && (
                <Button variant="contorno" onClick={() => setEditing(true)}>
                  Corrigir
                </Button>
              )}
              <Button
                variant="contorno"
                loading={
                  update.isPending && update.variables?.body.archived !== undefined
                }
                onClick={() =>
                  update.mutate({ id: claim.id, body: { archived: !claim.archivedAt } })
                }
              >
                {claim.archivedAt ? 'Reativar' : 'Arquivar'}
              </Button>
            </div>
          )
        }
      >
        {claim && <Headline claim={claim} />}
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {update.error && <Alert tone="erro">{update.error.message}</Alert>}

      {claim && editing && (
        <Card
          title="Corrigir o sinistro"
          descricao="Só o que foi contado na abertura. A fase se move pela análise e pelo reparo."
        >
          <ClaimForm
            mode={{ kind: 'editar', claim }}
            onSaved={() => setEditing(false)}
            onCancel={() => setEditing(false)}
          />
        </Card>
      )}

      {claim && addingThirdParty && (
        <Card
          title={`Terceiro envolvido no #${claim.id}`}
          descricao="O protocolo, quando e onde aconteceu e o reboque vêm do sinistro do associado, e podem ser corrigidos. A versão do terceiro começa vazia."
        >
          <ClaimForm
            mode={{ kind: 'terceiro', parent: claim }}
            onSaved={(created) => {
              setAddingThirdParty(false);
              void navigate({ to: '/claims/$id', params: { id: String(created.id) } });
            }}
            onCancel={() => setAddingThirdParty(false)}
          />
        </Card>
      )}

      {viewing && (
        <ClaimDetail
          claim={claim}
          onAddThirdParty={() => setAddingThirdParty(true)}
          onOpenClaim={(other) =>
            void navigate({ to: '/claims/$id', params: { id: String(other) } })
          }
        />
      )}
    </Page>
  );
}

/**
 * O miolo da ficha — o resumo e as seções gêmeas do `ClaimForm` —, sem a
 * moldura. A página (`ClaimScreen`) e o painel da lista (`ClaimPanel`) mostram
 * o MESMO miolo; o que muda é a moldura e o que acontece ao abrir outro
 * sinistro (`onOpenClaim`): a página navega, o painel troca de sinistro.
 */
export function ClaimDetail({
  claim,
  onAddThirdParty,
  onOpenClaim,
}: {
  claim: Claim;
  onAddThirdParty: () => void;
  onOpenClaim: (id: number) => void;
}) {
  return (
    <>
      <Summary claim={claim} />
      <DetailSections label={`Seções do sinistro #${claim.id}`}>
        <VehicleSection claim={claim} />
        <OccurrenceSection claim={claim} />
        <NoticeFilesSection claim={claim} />
        <PlaceSection claim={claim} />
        {/* O aviso do terceiro não pergunta o condutor (ver o `ClaimForm`). */}
        {claim.type !== 'terceiro' && <DriverSection claim={claim} />}
        <DynamicsSection claim={claim} />
        <TowSection claim={claim} />
        {claim.type !== 'terceiro' && <DeclaredSection claim={claim} />}
        {claim.type === 'associado' && (
          <ThirdPartiesSection
            claim={claim}
            onAdd={onAddThirdParty}
            onOpen={onOpenClaim}
          />
        )}
      </DetailSections>
    </>
  );
}

/* ------------------------------------------------------------------------- *
 * O resumo
 * ------------------------------------------------------------------------- */

/**
 * A linha de baixo do título: quando abriu e, no do terceiro, de qual sinistro
 * ele é. Com `onOpenClaim`, o pai abre no lugar (o painel); sem, é um link de
 * rota (a página).
 */
export function Headline({
  claim,
  onOpenClaim,
}: {
  claim: Claim;
  onOpenClaim?: (id: number) => void;
}) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="tabular-nums">Aberto em {formatInstant(claim.createdAt)}</span>
      {claim.type === 'terceiro' && claim.parentClaimId && (
        <>
          <span className="text-texto-suave">· ligado ao</span>
          {onOpenClaim ? (
            <button
              type="button"
              onClick={() => onOpenClaim(claim.parentClaimId!)}
              className={linkTextClasses()}
            >
              sinistro #{claim.parentClaimId}
            </button>
          ) : (
            <Link
              to="/claims/$id"
              params={{ id: String(claim.parentClaimId) }}
              className={linkTextClasses()}
            >
              sinistro #{claim.parentClaimId}
            </Link>
          )}
        </>
      )}
      {claim.archivedAt && <Badge tone="neutro">Arquivado</Badge>}
    </span>
  );
}

/** O modelo do carro do núcleo, numa linha: marca, modelo e ano. */
function vehicleModel(v: NonNullable<Claim['vehicle']>): string | null {
  return (
    [v.brand, v.model].filter(Boolean).join(' ') +
      (v.modelYear ? ` · ${v.modelYear}` : '') || null
  );
}

/** Quem é, e qual é o carro — do núcleo no do associado, do cadastro no do terceiro. */
function personOf(claim: Claim) {
  if (claim.type === 'terceiro') {
    return {
      name: claim.thirdParty?.name ?? null,
      plate: claim.thirdParty?.plate ?? null,
      model: claim.thirdParty?.model ?? null,
    };
  }
  const v = claim.vehicle;
  return {
    name: v?.memberName ?? null,
    plate: v?.plate ?? null,
    model: v ? vehicleModel(v) : null,
  };
}

/**
 * O cartão de cima: a pessoa, onde o sinistro está, e quatro dados.
 *
 * A fase é texto, e não uma régua de etapas: o quadro da análise tem desvios
 * (paralisados, sindicância, negados) que não são passos de uma fila, e uma
 * régua "5 de 12" diria que o sinistro está na metade de um caminho que ele
 * talvez nunca percorra.
 */
function Summary({ claim }: { claim: Claim }) {
  const person = personOf(claim);
  const terceiro = claim.type === 'terceiro';

  return (
    <section
      aria-label="Resumo do sinistro"
      className={cardClasses({ padding: false, className: 'overflow-hidden' })}
    >
      <div className="flex flex-wrap items-center gap-4 px-4 py-4">
        <Avatar nome={person.name} size="lg" tone="marca" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-texto-suave">
            <Icon nome={terceiro ? 'terceiro' : 'pessoa'} size={16} />
            {terceiro ? 'Sinistro do terceiro' : 'Sinistro do associado'}
          </span>
          <p className="text-base font-bold wrap-anywhere text-texto">
            {person.name ?? (
              <NotProvided>
                {terceiro ? 'terceiro não encontrado' : 'dono fora do cadastro da casa'}
              </NotProvided>
            )}
          </p>
        </div>
        <div className="flex w-full flex-col gap-1.5 sm:w-auto sm:items-end">
          <span className="text-xs font-medium text-texto-suave">Onde está</span>
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone="informacao">{DEPARTMENT_LABEL[claim.currentDepartment]}</Badge>
            <span className="text-sm font-semibold text-texto">
              {phaseLabel(claim.currentDepartment, claim.currentPhase)}
            </span>
            <ClosureBadge origin={claim.currentDepartment} phase={claim.currentPhase} />
          </span>
        </div>
      </div>

      {/* O traço entre os quatro é o `gap-px` sobre o fundo da borda: cada
          dado é uma célula da grade, e a linha se desenha sozinha em qualquer
          número de colunas. */}
      <dl className="grid gap-px border-t border-borda-suave bg-borda-suave sm:grid-cols-2 xl:grid-cols-4">
        <KeyFact icon="veiculo" label="Veículo">
          {person.plate ? (
            <>
              <span className="font-mono tabular-nums">{person.plate}</span>
              {person.model && (
                <span className="font-normal text-texto-suave"> · {person.model}</span>
              )}
            </>
          ) : (
            <NotProvided>sem placa</NotProvided>
          )}
        </KeyFact>
        <KeyFact icon="etiqueta" label="Tipo">
          {labelOf(CLAIM_TYPE_OPTIONS, claim.claimType)}
        </KeyFact>
        <KeyFact icon="calendario" label="Ocorrido em">
          <span className="tabular-nums">{formatInstant(claim.occurredAt)}</span>
        </KeyFact>
        <KeyFact icon="documento" label="Protocolo OPA">
          <span className="font-mono tabular-nums">{claim.opaSuiteProtocol}</span>
        </KeyFact>
      </dl>
    </section>
  );
}

function KeyFact({
  icon,
  label,
  children,
}: {
  icon: IconName;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-center gap-3 bg-superficie px-4 py-3">
      <span
        aria-hidden="true"
        className="flex size-8 shrink-0 items-center justify-center rounded-pequeno bg-superficie-suave text-texto-suave"
      >
        <Icon nome={icon} size={16} />
      </span>
      <div className="flex min-w-0 flex-col">
        <dt className="text-xs font-medium text-texto-suave">{label}</dt>
        <dd className="text-sm font-semibold wrap-anywhere text-texto">{children}</dd>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * As seções — os mesmos títulos e ícones do `ClaimForm`
 * ------------------------------------------------------------------------- */

/**
 * Uma resposta de lista, como selo. "Não" em cinza, o resto no tom de
 * informação: o que aconteceu é o que se procura numa ficha de sinistro.
 * Valor vazio é "não informado", e nunca um "Não" inventado.
 */
function Answer({ options, value }: { options: Option[]; value: string | null }) {
  if (!value) return <NotProvided />;
  return (
    <Badge tone={value === 'nao' ? 'neutro' : 'informacao'}>
      {labelOf(options, value)}
    </Badge>
  );
}

function orNot(value: string | null | undefined): ReactNode {
  return value ? value : <NotProvided />;
}

function VehicleSection({ claim }: { claim: Claim }) {
  if (claim.type === 'terceiro') {
    const tp = claim.thirdParty;
    return (
      <DetailSection icon="terceiro" title="O terceiro">
        {tp ? (
          <Facts>
            <Fact label="Terceiro">
              <span className="flex min-w-0 items-center gap-2">
                <Avatar nome={tp.name} size="sm" />
                <Link
                  to="/claims/third-parties/$id"
                  params={{ id: String(tp.id) }}
                  className={linkTextClasses()}
                >
                  {tp.name}
                </Link>
              </span>
            </Fact>
            <Fact label="Telefone">
              <span className="tabular-nums">{formatPhone(tp.phone)}</span>
            </Fact>
            <Fact label="Envolve veículo de terceiro">
              <Answer options={YES_NO} value={claim.reportedThirdPartyVehicle} />
            </Fact>
            {tp.plate && (
              <>
                <Fact label="Placa">
                  <span className="font-mono tabular-nums">{tp.plate}</span>
                </Fact>
                <Fact label="Modelo">{tp.model ?? <NotProvided />}</Fact>
              </>
            )}
            <ThirdPartyAnswers claim={claim} />
          </Facts>
        ) : (
          <p className="text-sm text-texto-suave">O terceiro não foi encontrado.</p>
        )}
      </DetailSection>
    );
  }

  const v = claim.vehicle;
  return (
    <DetailSection
      icon="veiculo"
      title="O veículo"
      descricao="Lido do cadastro da casa — nada aqui é copiado para o sinistro."
    >
      <Facts>
        <Fact label="Código no SGA">
          {claim.vehicleCode ? (
            <span className="font-mono tabular-nums">{claim.vehicleCode}</span>
          ) : (
            <NotProvided />
          )}
        </Fact>
        {v ? (
          <>
            <Fact label="Placa">
              {v.plate ? (
                <span className="font-mono tabular-nums">{v.plate}</span>
              ) : (
                <NotProvided>sem placa</NotProvided>
              )}
            </Fact>
            <Fact label="Veículo">{vehicleModel(v) ?? <NotProvided />}</Fact>
            <Fact label="Dono">
              {v.memberName ? (
                <span className="flex min-w-0 items-center gap-2">
                  <Avatar nome={v.memberName} size="sm" />
                  <span className="min-w-0 wrap-anywhere">{v.memberName}</span>
                </span>
              ) : (
                <NotProvided>sem nome no cadastro</NotProvided>
              )}
            </Fact>
          </>
        ) : (
          <Fact label="Placa e dono">
            <NotProvided>
              o código ainda não está no cadastro da casa — a placa e o dono aparecem
              quando o SGA for projetado
            </NotProvided>
          </Fact>
        )}
      </Facts>
    </DetailSection>
  );
}

/**
 * Quanto tempo depois do ocorrido a operação soube. Sai das duas datas
 * gravadas, e nada mais — é conta, não leitura.
 */
function noticeDelay(occurredAt: string, notifiedAt: string): string | null {
  const minutes = Math.round(
    (new Date(notifiedAt).getTime() - new Date(occurredAt).getTime()) / 60_000,
  );
  if (minutes < 0) return null;
  if (minutes < 60) return minutes === 0 ? 'na mesma hora' : `${minutes} min depois`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h depois`;
  const days = Math.floor(hours / 24);
  return `${days} dia${days > 1 ? 's' : ''} depois`;
}

function OccurrenceSection({ claim: c }: { claim: Claim }) {
  const delay = noticeDelay(c.occurredAt, c.notifiedAt);
  return (
    <DetailSection icon="relogio" title="A ocorrência">
      <Facts>
        <Fact label="Protocolo OPA Suite">
          <span className="font-mono tabular-nums">{c.opaSuiteProtocol}</span>
        </Fact>
        <Fact label="Tipo de sinistro">{labelOf(CLAIM_TYPE_OPTIONS, c.claimType)}</Fact>
        <Fact label="Ocorrido em">
          <span className="tabular-nums">{formatInstant(c.occurredAt)}</span>
        </Fact>
        <Fact label="Avisado em">
          <span className="tabular-nums">{formatInstant(c.notifiedAt)}</span>
          {delay && <span className="font-normal text-texto-suave"> · {delay}</span>}
        </Fact>
        <Fact label="Descrição do sinistro" wide>
          <p className="whitespace-pre-line rounded-borda bg-superficie-suave px-3 py-2.5 font-normal leading-relaxed">
            {c.description}
          </p>
        </Fact>
      </Facts>
    </DetailSection>
  );
}

/**
 * As imagens do ocorrido — a mesma etapa do formulário, com o mesmo ícone, e
 * no mesmo lugar: logo depois da ocorrência, que é o relato que elas mostram. A
 * galeria é a da casa: a prévia assina a URL só do que dá para desenhar, e o
 * visualizador abre foto, vídeo, áudio e PDF. Incluir e tirar arquivo é pela
 * correção, como todo outro dado do aviso.
 */
function NoticeFilesSection({ claim: c }: { claim: Claim }) {
  return (
    <DetailSection icon="imagem" title="Imagens do ocorrido">
      <AttachmentGallery
        label="Arquivos do aviso"
        items={c.noticeAttachments.map((d) => noticeAttachmentItem(c.id, d))}
        empty="O aviso não trouxe arquivo. Para enviar um, corrija o sinistro."
      />
    </DetailSection>
  );
}

function PlaceSection({ claim: c }: { claim: Claim }) {
  const a = c.occurrenceAddress;
  const cep = a?.zipCode?.replace(/^(\d{5})(\d{3})$/, '$1-$2');
  return (
    <DetailSection
      icon="mapa"
      title="O local"
      actions={
        a?.locationLink && (
          <a
            href={a.locationLink}
            target="_blank"
            rel="noreferrer"
            className={buttonClasses({ variant: 'contorno', size: 'sm' })}
          >
            <Icon nome="mapa" size={16} />
            Abrir no mapa
          </a>
        )
      }
    >
      <Facts>
        <Fact label="Endereço" wide>
          {orNot([a?.street, a?.number, a?.complement].filter(Boolean).join(', '))}
        </Fact>
        <Fact label="Bairro">{orNot(a?.neighborhood)}</Fact>
        <Fact label="Cidade">
          {a?.city ? `${a.city}${a.state ? `/${a.state}` : ''}` : <NotProvided />}
        </Fact>
        <Fact label="CEP">
          {cep ? <span className="tabular-nums">{cep}</span> : <NotProvided />}
        </Fact>
        <Fact label="Sentido">{orNot(c.direction)}</Fact>
        <Fact label="Câmeras no local">
          <Answer options={CAMERAS_OPTIONS} value={c.occurrenceHasCameras} />
        </Fact>
      </Facts>
    </DetailSection>
  );
}

function DriverSection({ claim: c }: { claim: Claim }) {
  return (
    <DetailSection icon="cracha" title="O condutor">
      <Facts>
        <Fact label="O condutor era o associado">
          <Answer options={YES_NO} value={c.driverIsMember} />
        </Fact>
        {c.driverIsMember === 'nao' && (
          <>
            <Fact label="Nome do condutor">
              {c.driverName ? (
                <span className="flex min-w-0 items-center gap-2">
                  <Avatar nome={c.driverName} size="sm" />
                  <span className="min-w-0 wrap-anywhere">{c.driverName}</span>
                </span>
              ) : (
                <NotProvided />
              )}
            </Fact>
            <Fact label="CPF do condutor">
              {c.driverDocument ? (
                <span className="tabular-nums">{formatDocument(c.driverDocument)}</span>
              ) : (
                <NotProvided />
              )}
            </Fact>
            <Fact label="Telefone do condutor">
              {c.driverPhone ? (
                <span className="tabular-nums">{formatPhone(c.driverPhone)}</span>
              ) : (
                <NotProvided />
              )}
            </Fact>
          </>
        )}
      </Facts>
    </DetailSection>
  );
}

function DynamicsSection({ claim: c }: { claim: Claim }) {
  return (
    <DetailSection icon="demandas" title="A dinâmica">
      <Facts>
        <Fact label="Causador do sinistro">
          <Answer options={CAUSED_BY_OPTIONS} value={c.causedBy} />
        </Fact>
        <Fact label="Avanço de preferencial">
          <Answer options={YES_NO} value={c.hadRightOfWayViolation} />
        </Fact>
        <Fact label="Órgão de trânsito acionado">
          <Answer options={YES_NO} value={c.trafficAuthorityNotified} />
        </Fact>
        {c.trafficAuthorityNotified === 'sim' && (
          <Fact label="Qual órgão">{orNot(c.trafficAuthorityDescription)}</Fact>
        )}
      </Facts>
    </DetailSection>
  );
}

function TowSection({ claim: c }: { claim: Claim }) {
  return (
    <DetailSection icon="bandeira" title="O reboque">
      <Facts>
        <Fact label="Acionou reboque">
          <Answer options={YES_NO} value={c.memberRequestedTow} />
        </Fact>
        <Fact label="Destino do veículo">
          {labelOf(TOW_DESTINATION_OPTIONS, c.towDestinationType) || <NotProvided />}
        </Fact>
        {c.towDestinationType === 'outro_local_indicado_pelo_associado' && (
          <Fact label="Endereço de destino">{orNot(c.towDestinationAddress)}</Fact>
        )}
      </Facts>
    </DetailSection>
  );
}

/**
 * O seguro ou o tipo de dano — a mesma regra do formulário: o que ele não
 * pergunta, a ficha não mostra.
 */
function ThirdPartyAnswers({ claim: c }: { claim: Claim }) {
  const asks = thirdPartyQuestions(c);
  return (
    <>
      {asks.asksInsurance && (
        <Fact label="O terceiro tem seguro">
          <Answer options={YES_NO} value={c.thirdPartyHasInsurance} />
        </Fact>
      )}
      {asks.asksDamageType && (
        <Fact label="Tipo de dano ao terceiro">
          {labelOf(THIRD_PARTY_DAMAGE_OPTIONS, c.thirdPartyDamageType) || <NotProvided />}
        </Fact>
      )}
    </>
  );
}

/**
 * O que o aviso do associado declarou: só se há terceiro (desde 05/10). O
 * resto é de cada terceiro, na ficha dele. O aviso aberto antes disso
 * respondeu também pelo terceiro, e a resposta continua aqui, dita como do
 * formulário antigo — o dado gravado não some da ficha.
 */
function DeclaredSection({ claim: c }: { claim: Claim }) {
  return (
    <DetailSection icon="terceiro" title="O que foi declarado sobre terceiros">
      <Facts>
        <Fact label="Envolve terceiros">
          <Answer
            options={REPORTED_THIRD_PARTIES_OPTIONS}
            value={c.reportedThirdParties}
          />
        </Fact>
        {c.reportedThirdPartyVehicle && (
          <>
            <Fact label="Envolve veículo de terceiro (formulário antigo)">
              <Answer options={YES_NO} value={c.reportedThirdPartyVehicle} />
            </Fact>
            <ThirdPartyAnswers claim={c} />
          </>
        )}
      </Facts>
    </DetailSection>
  );
}

function ThirdPartiesSection({
  claim,
  onAdd,
  onOpen,
}: {
  claim: Claim;
  onAdd: () => void;
  onOpen: (id: number) => void;
}) {
  const { canCreate } = useClaimPermissions();
  const count = claim.thirdPartyClaims.length;

  return (
    <DetailSection
      icon="terceiro"
      title="Terceiros envolvidos"
      descricao="Cada terceiro tem o próprio sinistro, ligado a este, com análise e reparo independentes."
      bleed
      actions={
        <>
          <Badge tone={count > 0 ? 'informacao' : 'neutro'}>
            <span className="tabular-nums">{count}</span>
          </Badge>
          {canCreate && !claim.archivedAt && (
            <Button variant="contorno" size="sm" icon="mais" onClick={onAdd}>
              Adicionar terceiro envolvido
            </Button>
          )}
        </>
      }
    >
      <Table
        label={`Sinistros de terceiro ligados ao #${claim.id}`}
        items={claim.thirdPartyClaims}
        loading={false}
        rowKey={(c) => String(c.id)}
        rowTitle={(c) => `Sinistro #${c.id}`}
        rowSubtitle={(c) => c.thirdParty?.name ?? ''}
        rowIcon="terceiro"
        rowActions={(c): Action[] => [
          {
            label: 'Abrir ficha',
            icon: 'seta-direita',
            run: () => onOpen(c.id),
          },
        ]}
        empty={
          claim.reportedThirdPartyVehicle === 'sim' ||
          claim.reportedThirdParties !== 'nao'
            ? 'Na abertura foi declarado terceiro, e nenhum sinistro de terceiro foi aberto ainda.'
            : 'Nenhum terceiro envolvido neste sinistro.'
        }
        columns={[
          {
            title: 'Sinistro',
            minWidth: '10rem',
            cell: (c) => (
              <DataCell icon="terceiro" hint="Sinistro do terceiro">
                <span className="font-semibold tabular-nums">#{c.id}</span>
                {c.archivedAt && <Badge tone="neutro">Arquivado</Badge>}
              </DataCell>
            ),
          },
          {
            title: 'Terceiro',
            alignment: 'inicio' as const,
            minWidth: '13rem',
            cell: (c) =>
              c.thirdParty ? (
                <div className="flex min-w-0 items-center gap-2.5">
                  <Avatar nome={c.thirdParty.name} size="md" />
                  <span className="truncate">{c.thirdParty.name}</span>
                </div>
              ) : (
                <NotProvided />
              ),
          },
          {
            title: 'Veículo',
            minWidth: '10rem',
            cell: (c) =>
              c.thirdParty ? (
                <DataCell icon="veiculo" hint={c.thirdParty.model ?? undefined}>
                  {c.thirdParty.plate ? (
                    <span className="font-mono tabular-nums">{c.thirdParty.plate}</span>
                  ) : (
                    <NotProvided>sem veículo</NotProvided>
                  )}
                </DataCell>
              ) : (
                <NotProvided />
              ),
          },
          {
            title: 'Onde está',
            minWidth: '13rem',
            cell: (c) => (
              <DataCell
                icon="quadro"
                hint={`${DEPARTMENT_LABEL[c.currentDepartment]} · ${phaseLabel(c.currentDepartment, c.currentPhase)}`}
              >
                <span>{DEPARTMENT_LABEL[c.currentDepartment]}</span>
                <span className="truncate text-texto-suave">
                  · {phaseLabel(c.currentDepartment, c.currentPhase)}
                </span>
              </DataCell>
            ),
          },
        ]}
      />
    </DetailSection>
  );
}
